import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDevices } from '../../contexts/DeviceContext';
import { CheckIcon, CloseIcon, SearchIcon, WatchIcon } from '../ui/Icons';
import { normalizeText } from '../../utils/faq/searchUtils';
import './DevicePicker.css';
/** Kurzname für den Header-Button, z. B. "fēnix 8 - 47 mm" → "fēnix 8" */
const shortName = (name) => name.split(/\s[-–/]\s|,|\(/)[0].trim();
/**
 * „Meine Uhr“ im Header: einmal auswählen, danach markieren alle Seiten passende Produkte.
 * Ohne Gerätedaten (API nicht erreichbar) wird der Button nicht angezeigt.
 */
const DevicePicker = () => {
    const { t } = useTranslation();
    const { available, devices, selected, select } = useDevices();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const panelRef = useRef(null);
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        inputRef.current?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                buttonRef.current?.focus();
            }
        };
        const onClick = (e) => {
            const target = e.target;
            if (panelRef.current && !panelRef.current.contains(target) && !buttonRef.current?.contains(target)) {
                setOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClick);
        document.addEventListener('touchstart', onClick);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('touchstart', onClick);
        };
    }, [open]);
    const filtered = useMemo(() => {
        const q = normalizeText(query.trim());
        if (!q)
            return devices;
        const terms = q.split(/\s+/);
        return devices.filter((d) => {
            const n = normalizeText(d.name);
            return terms.every((term) => n.includes(term));
        });
    }, [devices, query]);
    if (!available)
        return null;
    const choose = (id) => {
        select(id);
        setOpen(false);
        setQuery('');
        buttonRef.current?.focus();
    };
    return (_jsxs("div", { className: "device-picker", children: [_jsxs("button", { ref: buttonRef, type: "button", className: `icon-btn device-picker-btn ${selected ? 'has-device' : ''}`, "aria-haspopup": "dialog", "aria-expanded": open, onClick: () => setOpen((o) => !o), title: selected ? selected.name : t('pages.devices.choose'), "aria-label": selected ? `${t('pages.devices.myWatch')}: ${selected.name}` : t('pages.devices.choose'), children: [_jsx(WatchIcon, { size: 19 }), selected && _jsx("span", { className: "device-picker-name", children: shortName(selected.name) })] }), open && (_jsxs("div", { className: "device-panel", ref: panelRef, role: "dialog", "aria-modal": "false", "aria-labelledby": "device-panel-title", children: [_jsxs("div", { className: "device-panel-head", children: [_jsx("h2", { id: "device-panel-title", children: t('pages.devices.choose') }), _jsx("button", { type: "button", className: "icon-btn", onClick: () => setOpen(false), "aria-label": t('pages.themes.close'), children: _jsx(CloseIcon, { size: 18 }) })] }), _jsx("p", { className: "device-panel-hint", children: t('pages.devices.chooseHint') }), _jsxs("div", { className: "device-search", children: [_jsx(SearchIcon, { size: 18 }), _jsx("input", { ref: inputRef, id: "device-search", type: "search", value: query, onChange: (e) => setQuery(e.target.value), placeholder: t('pages.devices.searchPlaceholder'), "aria-label": t('pages.devices.searchPlaceholder'), autoComplete: "off" })] }), selected && (_jsxs("button", { type: "button", className: "device-clear", onClick: () => choose(null), children: [t('pages.devices.clear'), ": ", selected.name] })), _jsxs("ul", { className: "device-list", role: "listbox", "aria-label": t('pages.devices.choose'), children: [filtered.length === 0 && _jsx("li", { className: "device-empty", children: t('pages.devices.none') }), filtered.map((d) => (_jsx("li", { children: _jsxs("button", { type: "button", role: "option", "aria-selected": selected?.id === d.id, className: `device-option ${selected?.id === d.id ? 'active' : ''}`, onClick: () => choose(d.id), children: [_jsx("span", { children: d.name }), selected?.id === d.id && _jsx(CheckIcon, { size: 16 })] }) }, d.id)))] })] }))] }));
};
export default DevicePicker;
